import { inject, Pipe, type PipeTransform } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { type CanActivateFn, Router } from '@angular/router';
import type { UserRole } from '@shared/models/auth';
import { first, map } from 'rxjs';
import { AuthUser } from './auth-user';
import type { User } from '@shared/models/user';
import { hasRole } from '@shared/auth/has-role';

export const hasAccessGuard =
  (role: UserRole, orgId?: string, branchId?: string) => () => {
    const user = inject(AuthUser);
    const router = inject(Router);
    const user$ = toObservable(user.data);
    return user$.pipe(
      first((data) => !!data),
      map((data) => {
        const rolesList = data?.roles || [];
        if (rolesList.length === 0) {
          return router.createUrlTree(['/onboarding-wizard']);
        }

        if (!hasRole(data, role, orgId, branchId)) {
          // Return false to allow the router to try the next matching route
          return router.createUrlTree(['/error/403']);
        }
        return true;
      }),
    );
  };

export const staffGuard: CanActivateFn = (route) => {
  const orgId =
    route.paramMap.get('orgId') ||
    route.queryParamMap.get('orgId') ||
    undefined;
  const branchId =
    route.paramMap.get('branchId') ||
    route.queryParamMap.get('branchId') ||
    undefined;
  return hasAccessGuard('viewer', orgId, branchId)();
};

@Pipe({
  name: 'hasAccess',
  pure: false,
})
export class HasAccessPipe implements PipeTransform {
  readonly #user = inject(AuthUser);

  transform(role: UserRole, orgId?: string, branchId?: string) {
    const data = this.#user.data();
    if (!data) return false;
    return hasRole(data, role, orgId, branchId);
  }
}
